import type { RouteObject } from 'react-router';

import { lazy, Suspense } from 'react';
import { Outlet, Navigate } from 'react-router';

import { isNonProd } from 'src/utils/env';

import { CONFIG } from 'src/global-config';
import { DashboardLayout } from 'src/layouts/dashboard';

import { LoadingScreen } from 'src/components/loading-screen';

import { Combo } from 'src/sections/employer-data/combo/combo';
import { Policy } from 'src/sections/employer-data/policy/policy';
import { Coverage } from 'src/sections/employer-data/coverages/coverage';
import { ComboList } from 'src/sections/employer-data/combo-list/combo-list';
import { PolicyDetails } from 'src/sections/employer-data/policy-details/policy-details';
import { UsrDetails } from 'src/sections/employer-data/policy-details/usr-details/usr-details';
import AssignedRiskApplication from 'src/sections/employer-data/assigned-risk-application/assigned-risk-application';

import { AuthGuard } from 'src/auth/guard';

import { paths } from '../paths';
import { usePathname } from '../hooks';

// ----------------------------------------------------------------------

const EmployerData = lazy(() => import('src/pages/dashboard/employer-data'));
const SupportQueue = lazy(() => import('src/pages/dashboard/support-queue'));
const AdvancedSearch = lazy(() => import('src/pages/dashboard/advanced-search'));
const RejectedTransactions = lazy(() => import('src/pages/dashboard/rejected-transactions'));
const UnitStatReport = lazy(() => import('src/pages/dashboard/unit-stat-report'));

// ----------------------------------------------------------------------

function SuspenseOutlet() {
  const pathname = usePathname();
  return (
    <Suspense key={pathname} fallback={<LoadingScreen />}>
      <Outlet />
    </Suspense>
  );
}

const dashboardLayout = () => (
  <DashboardLayout>
    <SuspenseOutlet />
  </DashboardLayout>
);

export const dashboardRoutes: RouteObject[] = [
  {
    path: 'dashboard',
    element: CONFIG.auth.skip ? dashboardLayout() : <AuthGuard>{dashboardLayout()}</AuthGuard>,
    children: [
      { path: paths.dashboard.support_queue, element: <SupportQueue /> },
      { path: paths.dashboard.advanced_search, element: <AdvancedSearch /> },
      {
        path: paths.dashboard.employer_data.root,
        element: isNonProd() ? <EmployerData /> : <Navigate to="combo" />,
        children: isNonProd() ? [{ index: true, element: <ComboList /> }] : undefined,
      },
      {
        path: `${paths.dashboard.employer_data.combo}/:comboId`,
        element: <EmployerData />,
        children: [
          { element: <Combo />, index: true },
          { path: 'coverage/:coverageId', element: <Coverage /> },
          { path: 'coverage/:coverageId/policy/:policyId', element: <Policy /> },
          {
            path: 'coverage/:coverageId/policy/:policyId/policy-details',
            element: <PolicyDetails />,
          },
          {
            path: 'coverage/:coverageId/policy/:policyId/assigned-risk-application/:assignedRiskAppId',
            element: <AssignedRiskApplication />,
          },
          {
            path: 'coverage/:coverageId/assigned-risk-application/:assignedRiskAppId',
            element: <AssignedRiskApplication />,
          },
          {
            path: 'coverage/:coverageId/policy/:policyId/policy-details/usr-details/:reportNumber/:correctionSequenceNumber',
            element: <UsrDetails />,
          },
        ],
      },
      {
        path: paths.dashboard.data_management.rejected_transactions,
        element: <RejectedTransactions />,
      },
      {
        path: `${paths.dashboard.unit_stat_reports.root}/:id`,
        element: <UnitStatReport />,
      },
    ],
  },
];
